// src/containers/organization/forms/reportSettings.js
//
// Organisation-level Report Assistance preferences card.
//
// Displayed on the Organisation > Settings tab (Alpha Tester gate).
// Controls the master flag and two sub-type checkboxes:
//
//   reportAssistEnabled — master toggle: shows all assisted content types
//     showWriterNote    — suggested writing prompts in WriterNote areas
//     showChartProse    — grey ChartProse chart-description boxes
//
//   llmAssistEnabled — allow Claude LLM narrative generation (requires assist on)
//
//   reportWordTemplateUrl — org default .dotx Word export template (stub)
//
// Sub-type checkboxes are only shown (and only meaningful) when the master
// toggle is on. Both default true — enabling the master shows everything.
//
// The amber AuthorComment instructional blocks are unrelated to this preference
// panel — they're still gated by showAuthorComment internally, but there is no
// org-level toggle for it (it simply follows the master switch). The blue
// "required but missing" notices in the report (Background, Reflection) are a
// separate, always-on feature — they reflect the activity's real completion
// state and are not part of the writing-assist system at all.

/* eslint-disable react-hooks/exhaustive-deps */
import { useTranslation, withTranslation } from 'react-i18next';
import { useState, useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Checkbox, Radio, Segment } from 'semantic-ui-react';
import axios from 'axios';

import { get } from '../../../utils/lodash';
import { updateOrgReportSettings } from '../../../store/actions';
import { authMsg } from '../../../utils/auth';

import './organization.scss';

// TAK-2088: the "Takso assisted analysis" (machine-reading draft narratives)
// toggle is hidden until that feature exists. Set to true to show it again —
// and review its wording then.
const SHOW_TAKSO_ASSISTED_ANALYSIS = false;

// ── StatementEditor ───────────────────────────────────────────────────────────
// contentEditable rich-text field with a minimal format toolbar and an
// expand/collapse button. Stores HTML; calls onChange(html) on every edit.
//
// Props:
//   value      — initial HTML string (set once on first non-empty load)
//   onChange   — (html: string) => void — fires on every input event
//   placeholder — string shown when empty
//   collapsedHeight — px height when collapsed (default 120)

const TOOLBAR_ACTIONS = [
  { cmd: 'bold', label: <strong>B</strong>, title: 'Bold (Ctrl+B)' },
  { cmd: 'italic', label: <em>I</em>, title: 'Italic (Ctrl+I)' },
  { cmd: 'underline', label: <u>U</u>, title: 'Underline (Ctrl+U)' },
  null, // separator
  { cmd: 'insertUnorderedList', label: '• List', title: 'Bullet list' },
  { cmd: 'insertOrderedList', label: '1. List', title: 'Numbered list' },
];

function StatementEditor({
  value,
  onChange,
  placeholder,
  collapsedHeight = 120,
}) {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const initialised = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const [activeCommands, setActiveCommands] = useState({});

  // Set innerHTML once when the API value first arrives
  useEffect(() => {
    if (!initialised.current && value && editorRef.current) {
      editorRef.current.innerHTML = value;
      initialised.current = true;
    }
  }, [value]);

  // Update active-command highlights whenever cursor moves or selection changes
  const refreshActive = useCallback(() => {
    const cmds = {};
    [
      'bold',
      'italic',
      'underline',
      'insertUnorderedList',
      'insertOrderedList',
    ].forEach((cmd) => {
      try {
        cmds[cmd] = document.queryCommandState(cmd);
      } catch (e) {
        /* noop */
      }
    });
    setActiveCommands(cmds);
  }, []);

  const handleInput = useCallback(() => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  }, [onChange]);

  // Toolbar button: prevent blur, apply command, re-focus
  const handleFormat = useCallback(
    (cmd) => {
      editorRef.current && editorRef.current.focus();
      document.execCommand(cmd, false, null);
      handleInput();
      refreshActive();
    },
    [handleInput, refreshActive]
  );

  // Expand to full content height; collapse back to collapsedHeight
  const toggleExpand = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    if (!expanded) {
      el.style.maxHeight = el.scrollHeight + 'px';
      el.style.height = 'auto';
    } else {
      el.style.maxHeight = collapsedHeight + 'px';
      el.style.height = collapsedHeight + 'px';
    }
    setExpanded((v) => !v);
  }, [expanded, collapsedHeight]);

  // Apply initial collapsed height on first render
  const bodyStyle = expanded
    ? { height: 'auto' }
    : {
        maxHeight: collapsedHeight,
        height: collapsedHeight,
        overflow: 'hidden',
      };

  return (
    <div className="stmt-editor">
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div className="stmt-editor__toolbar">
        {TOOLBAR_ACTIONS.map((action, i) =>
          action === null ? (
            <span key={`sep-${i}`} className="stmt-editor__sep" />
          ) : (
            <button
              key={action.cmd}
              type="button"
              title={action.title}
              className={`stmt-editor__btn${
                activeCommands[action.cmd] ? ' stmt-editor__btn--active' : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault(); // keep focus in editor
                handleFormat(action.cmd);
              }}
            >
              {action.label}
            </button>
          )
        )}
      </div>

      {/* ── Editable body ───────────────────────────────────────────────── */}
      <div
        ref={editorRef}
        className="stmt-editor__body"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        style={bodyStyle}
        onInput={handleInput}
        onKeyUp={refreshActive}
        onMouseUp={refreshActive}
      />

      {/* ── Expand / Collapse footer ─────────────────────────────────────── */}
      <div className="stmt-editor__footer">
        <button
          type="button"
          className="stmt-editor__expand-btn"
          onClick={toggleExpand}
          title={
            expanded
              ? t('html_editors.collapse_description')
              : t('html_editors.expand_to_fit_description')
          }
        >
          {expanded
            ? `▲ ${t('html_editors.collapse')}`
            : `▼ ${t('html_editors.expand_to_fit')}`}
        </button>
      </div>
    </div>
  );
}

// ── Default sub-prefs (all on) ────────────────────────────────────────────────
const DEFAULT_OPTIONS = {
  showWriterNote: true,
  showChartProse: true,
};

const ToggleRow = ({ label, description, checked, onChange, disabled }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
      padding: '14px 0',
      borderBottom: '1px solid #f3f4f6',
    }}
  >
    <div style={{ flex: 1 }}>
      <div
        style={{
          fontWeight: 600,
          fontSize: 14,
          color: '#111827',
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
        {description}
      </div>
    </div>
    <div style={{ flexShrink: 0, paddingTop: 2 }}>
      <Radio toggle checked={checked} onChange={onChange} disabled={disabled} />
    </div>
  </div>
);

// Sub-type checkboxes rendered below the Takso assisted toggle when it is on
const SubOptionRow = ({ label, description, checked, onChange }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: '8px 0 8px 16px',
      borderBottom: '1px solid #f9fafb',
    }}
  >
    <Checkbox
      checked={checked}
      onChange={onChange}
      style={{ flexShrink: 0, marginTop: 2 }}
    />
    <div>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 11,
          color: '#9ca3af',
          lineHeight: 1.4,
          marginTop: 1,
        }}
      >
        {description}
      </div>
    </div>
  </div>
);

const OrgReportSettings = ({
  t,
  auth,
  organization,
  updateOrgReportSettings,
}) => {
  const org = get(auth, 'user.organisation', {});
  const betaTester = Boolean(org.isBetaTester);

  const [reportAssistEnabled, setReportAssistEnabled] = useState(
    Boolean(org.reportAssistEnabled)
  );
  const [llmAssistEnabled, setLlmAssistEnabled] = useState(
    Boolean(org.llmAssistEnabled)
  );

  // Sub-type options — merge DB value with defaults so missing keys default true
  const [assistOptions, setAssistOptions] = useState({
    ...DEFAULT_OPTIONS,
    ...(org.reportAssistOptions || {}),
  });

  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // ── Public statement ───────────────────────────────────────────────────────
  const [publicStatementTitle, setPublicStatementTitle] = useState('');
  const [publicStatement, setPublicStatement] = useState('');
  const [statementDirty, setStatementDirty] = useState(false);
  const [statementSaving, setStatementSaving] = useState(false);

  // ── End signature ──────────────────────────────────────────────────────────
  const [endSignatureUrl, setEndSignatureUrl] = useState(null);
  const [signatureUploading, setSignatureUploading] = useState(false);
  const [signatureDeleting, setSignatureDeleting] = useState(false);
  const signatureInputRef = useRef(null);

  // ── Graded outcome measures default (beta) ──────────────────────────────────
  const [useGradedOutcomes, setUseGradedOutcomes] = useState(false);
  const [gradedDirty, setGradedDirty] = useState(false);
  const [gradedSaving, setGradedSaving] = useState(false);

  useEffect(() => {
    if (!get(auth, 'user.organisation.id')) return;
    axios
      .get('/organisation/report-builder-preferences')
      .then((res) => {
        const overrides = res.data?.orgOverrides || {};
        setPublicStatementTitle(overrides.publicStatementTitle || '');
        setPublicStatement(overrides.publicStatement || '');
        setEndSignatureUrl(overrides.endSignatureUrl || null);
        setUseGradedOutcomes(Boolean(overrides.outcomes?.useGraded));
      })
      .catch(() => {});
  }, [get(auth, 'user.organisation.id')]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveStatement = async () => {
    setStatementSaving(true);
    try {
      // Read-modify-write: fetch current prefs first so we don't clobber
      // any report-builder display preferences saved via PreferencesTab.
      const current = await axios.get(
        '/organisation/report-builder-preferences'
      );
      const existing = current.data?.orgOverrides || {};
      await axios.put('/organisation/report-builder-preferences', {
        ...existing,
        publicStatementTitle: publicStatementTitle.trim() || null,
        publicStatement: publicStatement.trim() || null,
      });
      setStatementDirty(false);
      authMsg('success', [
        t('report.settings.corporate_public_statement.saved'),
        t('report.settings.title'),
      ]);
    } catch (e) {
      console.error('Failed to save statement', e);
      authMsg(
        'error',
        t('report.settings.corporate_public_statement.failed_to_save')
      );
    } finally {
      setStatementSaving(false);
    }
  };

  const handleSignatureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSignatureUploading(true);
    try {
      const formData = new FormData();
      formData.append('signature', file);
      const res = await axios.post(
        '/organisation/report-builder-signature',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setEndSignatureUrl(res.data.endSignatureUrl);
      authMsg('success', [
        t('report.settings.end_signature.saved'),
        t('report.settings.title'),
      ]);
    } catch (e) {
      authMsg('error', t('report.settings.end_signature.failed_to_upload'));
    } finally {
      setSignatureUploading(false);
      if (signatureInputRef.current) signatureInputRef.current.value = '';
    }
  };

  const handleSaveGraded = async () => {
    setGradedSaving(true);
    try {
      // Read-modify-write: fetch current prefs first so we don't clobber
      // any report-builder display preferences saved via PreferencesTab.
      const current = await axios.get(
        '/organisation/report-builder-preferences'
      );
      const existing = current.data?.orgOverrides || {};
      await axios.put('/organisation/report-builder-preferences', {
        ...existing,
        outcomes: { ...existing.outcomes, useGraded: useGradedOutcomes },
      });
      setGradedDirty(false);
      authMsg('success', [
        t('report.settings.graded_outcome_measures.saved'),
        t('report.settings.title'),
      ]);
    } catch (e) {
      console.error('Failed to save graded outcome measures setting', e);
      authMsg(
        'error',
        t('report.settings.graded_outcome_measures.failed_to_save')
      );
    } finally {
      setGradedSaving(false);
    }
  };

  const handleSignatureDelete = async () => {
    setSignatureDeleting(true);
    try {
      await axios.delete('/organisation/report-builder-signature');
      setEndSignatureUrl(null);
      authMsg('success', [
        t('report.settings.end_signature.removed'),
        t('report.settings.title'),
      ]);
    } catch (e) {
      authMsg('error', t('report.settings.end_signature.failed_to_remove'));
    } finally {
      setSignatureDeleting(false);
    }
  };

  // Sync when auth org data changes (after successful save or login)
  useEffect(() => {
    const orgData = get(auth, 'user.organisation', {});
    setReportAssistEnabled(Boolean(orgData.reportAssistEnabled));
    setLlmAssistEnabled(Boolean(orgData.llmAssistEnabled));
    setAssistOptions({
      ...DEFAULT_OPTIONS,
      ...(orgData.reportAssistOptions || {}),
    });
    setDirty(false);
  }, [
    get(auth, 'user.organisation.reportAssistEnabled'),
    get(auth, 'user.organisation.llmAssistEnabled'),
    get(auth, 'user.organisation.reportAssistOptions'),
  ]);

  const handleToggleAssist = () => {
    const next = !reportAssistEnabled;
    setReportAssistEnabled(next);
    // If Takso assistance is turned off, LLM must also be off
    if (!next) setLlmAssistEnabled(false);
    setDirty(true);
  };

  const handleToggleLlm = () => {
    setLlmAssistEnabled((v) => !v);
    setDirty(true);
  };

  const handleSubOption = (key) => {
    setAssistOptions((prev) => ({ ...prev, [key]: !prev[key] }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateOrgReportSettings({
        reportAssistEnabled,
        llmAssistEnabled,
        reportWordTemplateUrl: org.reportWordTemplateUrl || null,
        reportAssistOptions: assistOptions,
      });
      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  const loading = get(organization, 'orgLoading', false);

  return (
    <Segment loading={loading}>
      <div style={{ maxWidth: 680 }}>
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: '0 16px',
            marginBottom: 20,
            background: '#fff',
          }}
        >
          {/* ── Master: Takso assisted descriptions ─────────────────────────── */}
          <ToggleRow
            label={t('report.settings.takso_assisted_descriptions.label')}
            description={t(
              'report.settings.takso_assisted_descriptions.description'
            )}
            checked={reportAssistEnabled}
            onChange={handleToggleAssist}
          />

          {/* ── Sub-type checkboxes (visible only when master toggle is on) ─── */}
          {reportAssistEnabled && (
            <div
              style={{
                background: '#f9fafb',
                borderRadius: 6,
                margin: '0 -16px',
                padding: '4px 16px 4px 16px',
                borderBottom: '1px solid #f3f4f6',
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#9ca3af',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  padding: '8px 0 4px 16px',
                }}
              >
                {t(
                  'report.settings.takso_assisted_descriptions.show_in_reports'
                )}
              </div>
              <SubOptionRow
                label={t(
                  'report.settings.takso_assisted_descriptions.writing_prompts.label'
                )}
                description={t(
                  'report.settings.takso_assisted_descriptions.writing_prompts.description'
                )}
                checked={assistOptions.showWriterNote}
                onChange={() => handleSubOption('showWriterNote')}
              />
              <SubOptionRow
                label={t(
                  'report.settings.takso_assisted_descriptions.charts.label'
                )}
                description={t(
                  'report.settings.takso_assisted_descriptions.charts.description'
                )}
                checked={assistOptions.showChartProse}
                onChange={() => handleSubOption('showChartProse')}
              />
            </div>
          )}

          {/* ── LLM analysis (hidden until the feature exists — TAK-2088) ──── */}
          {SHOW_TAKSO_ASSISTED_ANALYSIS && (
            <ToggleRow
              label={t('report.settings.takso_assisted_analysis.title')}
              description={t(
                'report.settings.takso_assisted_analysis.description'
              )}
              checked={llmAssistEnabled}
              onChange={handleToggleLlm}
              disabled={!reportAssistEnabled}
            />
          )}

          {/* ── Word Template stub ───────────────────────────────────────────── */}
          <div
            style={{
              padding: '14px 0',
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 16,
            }}
          >
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: '#111827',
                  marginBottom: 3,
                }}
              >
                {t('report.settings.template.title')}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>
                {t('report.settings.template.upload_description')}
              </div>
              {org.reportWordTemplateUrl && (
                <div style={{ marginTop: 6, fontSize: 12, color: '#1d4ed8' }}>
                  {t('report.settings.template.current')}:{' '}
                  {org.reportWordTemplateUrl.split('/').pop()}
                </div>
              )}
            </div>
            <div style={{ flexShrink: 0, paddingTop: 2 }}>
              <Button size="tiny" disabled>
                {t('report.settings.template.upload')}
              </Button>
            </div>
          </div>
        </div>

        {/* ── Save ──────────────────────────────────────────────────────────── */}
        <Button
          className="Primary-Button"
          onClick={handleSave}
          loading={saving}
          disabled={saving || !dirty}
        >
          {t('report.settings.save_report_settings')}
        </Button>
        {dirty && (
          <span style={{ marginLeft: 12, fontSize: 12, color: '#f59e0b' }}>
            {t('formulary.unsaved_changes')}
          </span>
        )}

        {/* ── Corporate public statement ──────────────────────────────────── */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 16,
            marginTop: 20,
            background: '#fff',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: '#111827',
              marginBottom: 4,
            }}
          >
            {t('report.settings.corporate_public_statement.title')}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#6b7280',
              lineHeight: 1.5,
              marginBottom: 10,
            }}
          >
            {t('report.settings.corporate_public_statement.description')}
          </div>
          <input
            className="stmt-editor__title-input"
            type="text"
            value={publicStatementTitle}
            onChange={(e) => {
              setPublicStatementTitle(e.target.value);
              setStatementDirty(true);
            }}
            placeholder={t(
              'report.settings.corporate_public_statement.section_heading'
            )}
            maxLength={120}
          />
          <StatementEditor
            value={publicStatement}
            onChange={(html) => {
              setPublicStatement(html);
              setStatementDirty(true);
            }}
            placeholder={t(
              'report.settings.corporate_public_statement.placeholder'
            )}
          />
          <div style={{ marginTop: 10 }}>
            <Button
              className="Primary-Button"
              onClick={handleSaveStatement}
              loading={statementSaving}
              disabled={statementSaving || !statementDirty}
              size="small"
            >
              {t('report.settings.save_statement')}
            </Button>
            {statementDirty && (
              <span style={{ marginLeft: 12, fontSize: 12, color: '#f59e0b' }}>
                {t('formulary.unsaved_changes')}
              </span>
            )}
          </div>
        </div>
        {/* ── End signature ───────────────────────────────────────────────── */}
        <div
          style={{
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            padding: 16,
            marginTop: 20,
            background: '#fff',
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: 14,
              color: '#111827',
              marginBottom: 4,
            }}
          >
            {t('report.settings.end_signature.label')}
          </div>
          <div
            style={{
              fontSize: 12,
              color: '#6b7280',
              lineHeight: 1.5,
              marginBottom: 12,
            }}
          >
            {t('report.settings.upload_end_signature_description')}
          </div>

          {endSignatureUrl ? (
            <div>
              <div
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  padding: 12,
                  background: '#f9fafb',
                  marginBottom: 10,
                  textAlign: 'center',
                }}
              >
                <img
                  src={endSignatureUrl}
                  alt="End signature"
                  style={{ maxWidth: '100%', maxHeight: 160 }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  size="tiny"
                  onClick={() => signatureInputRef.current?.click()}
                  loading={signatureUploading}
                  disabled={signatureUploading || signatureDeleting}
                >
                  {t('actions.replace_image')}
                </Button>
                <Button
                  size="tiny"
                  color="red"
                  basic
                  onClick={handleSignatureDelete}
                  loading={signatureDeleting}
                  disabled={signatureUploading || signatureDeleting}
                >
                  {t('actions.remove')}
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="tiny"
              onClick={() => signatureInputRef.current?.click()}
              loading={signatureUploading}
              disabled={signatureUploading}
            >
              {t('report.settings.upload_signature_image')}
            </Button>
          )}

          <input
            ref={signatureInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleSignatureUpload}
          />
        </div>

        {/* ── Graded outcome measures default (beta) ────────────────────────── */}
        {betaTester && (
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 8,
              padding: '0 16px',
              marginTop: 20,
              background: '#fff',
            }}
          >
            <ToggleRow
              label={t('report.settings.graded_outcome_measures.label')}
              description={t(
                'report.settings.graded_outcome_measures.description'
              )}
              checked={useGradedOutcomes}
              onChange={() => {
                setUseGradedOutcomes((v) => !v);
                setGradedDirty(true);
              }}
            />
            <div style={{ padding: '14px 0' }}>
              <Button
                className="Primary-Button"
                onClick={handleSaveGraded}
                loading={gradedSaving}
                disabled={gradedSaving || !gradedDirty}
                size="small"
              >
                {t('actions.save')}
              </Button>
              {gradedDirty && (
                <span
                  style={{ marginLeft: 12, fontSize: 12, color: '#f59e0b' }}
                >
                  {t('formulary.unsaved_changes')}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </Segment>
  );
};

const mapStateToProps = (state) => ({
  auth: state.authentication,
  organization: state.organization,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ updateOrgReportSettings }, dispatch);

export const OrganizationReportSettings = connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(OrgReportSettings));
